import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Download, Upload, FileText, CheckCircle2, AlertTriangle,
  XCircle, Database, ChevronDown, ChevronRight, Eye, Trash2, Plus, X,
  Search, RefreshCcw, Tag,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import MealPreviewRenderer from './MealPreviewRenderer';
import { adminAPI } from '../../utils/api';
import {
  CORE,
  INLINE,
  provenanceOnImportHydrate,
} from '../../utils/macrosProvenance';
import { BACKEND_BASE_URL } from '../../config/backend';
import {
  COOKWARE_OPTIONS, CUISINE_OPTIONS, PROTEIN_OPTIONS, DEFAULT_UNIQUENESS_GROUPS,
} from '../../constants/mealOptions';

/**
 * Admin · Bulk meal import (CSV) — full-payload editable review.
 *
 * Flow:
 *   1. Download template + foods_core list.
 *   2. Upload CSV → /api/admin/meal-import/run (dry_run=true) returns the full
 *      parsed payload per meal (meal_input + ingredients with resolved_food_name).
 *   3. Review — each meal is an expandable card where EVERY field is editable
 *      using the SAME controls as the manual MealEditor (shared option lists,
 *      typeahead ingredient search, uniqueness-group combobox). A "Preview"
 *      button shows the meal the way a client will see it.
 *   4. Confirm — the edited JSON (only included meals) is POSTed to
 *      /api/admin/meal-import/commit which calls the same `_create_meal_v2_core`
 *      helper as the manual editor.
 */
export default function AdminMealImport() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // `meals` holds the editable per-meal state during review.
  // Each item: { ...dryRunRow, _included: bool, _expanded: bool }
  const [meals, setMeals] = useState(null);
  const [parseErrors, setParseErrors] = useState([]);
  const [confirmResult, setConfirmResult] = useState(null);
  const [previewMealKey, setPreviewMealKey] = useState(null); // which meal is in the user-view modal

  // Shared option state — same lists the MealEditor loads on mount so admins
  // pick from identical values regardless of which authoring path they use.
  const [uniquenessGroups, setUniquenessGroups] = useState(DEFAULT_UNIQUENESS_GROUPS);
  const [existingTags, setExistingTags] = useState([]);

  const backendUrl = BACKEND_BASE_URL;
  const sessionToken = typeof window !== 'undefined' ? window.localStorage.getItem('fitxess_session_token') : null;
  const authHeaders = sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};

  // Load uniqueness groups + existing tags once admin lands on this page so
  // the review cards can surface them immediately (matches MealEditor).
  useEffect(() => {
    adminAPI.listUniquenessGroups()
      .then((res) => {
        const groups = (res.data || []).map((g) => g.name || g);
        const merged = [...new Set([...DEFAULT_UNIQUENESS_GROUPS, ...groups])].sort();
        setUniquenessGroups(merged);
      })
      .catch(() => setUniquenessGroups(DEFAULT_UNIQUENESS_GROUPS));
    adminAPI.listTags()
      .then((res) => setExistingTags(res.data || []))
      .catch(() => setExistingTags([]));
  }, []);

  const handlePickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) { toast.error('Please upload a .csv file'); return; }
    setFile(f);
    setMeals(null);
    setParseErrors([]);
    setConfirmResult(null);
  };

  // -------- 1) CSV dry-run --------
  const runDryRun = async () => {
    if (!file) { toast.error('Choose a CSV file first'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('dry_run', 'true');
      const res = await fetch(`${backendUrl}/api/admin/meal-import/run`, {
        method: 'POST', headers: authHeaders, body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || `Preview failed (HTTP ${res.status})`); return; }
      setMeals(data.meals.map((m) => ({
        ...m,
        // Hydrate ingredients with per-gram macros + portion_options from dry-run
        // so they behave exactly like MealEditor-added ingredients on save.
        ingredients: (m.ingredients || []).map((ing) => hydrateIngredient(ing)),
        _included: m.errors.length === 0,
        _expanded: false,
      })));
      setParseErrors(data.parse_errors || []);
      setConfirmResult(null);
      const validCount = data.valid_meals;
      const totalCount = data.total_meals;
      const previewMsg = `Preview ready — ${validCount}/${totalCount} meals valid`;
      if (validCount === 0) {
        toast.error(previewMsg);
      } else if (validCount < totalCount) {
        toast.warning(previewMsg);
      } else {
        toast.success(previewMsg);
      }
    } catch (e) {
      console.error('[MealImport] dry-run error', e);
      toast.error('Network error — see console');
    } finally {
      setUploading(false);
    }
  };

  // -------- 2) Commit edited JSON --------
  const commitEdits = async () => {
    const included = (meals || []).filter((m) => m._included);
    const withErrors = included.filter((m) => (m.errors || []).length > 0);
    if (withErrors.length > 0) {
      toast.error(`${withErrors.length} included meal(s) still have errors — fix or exclude them first`);
      return;
    }
    if (included.length === 0) { toast.error('No meals included'); return; }

    setUploading(true);
    try {
      const payload = {
        meals: included.map((m) => ({
          meal_key: m.meal_key,
          meal_input: m.meal_input,
          // Mirror MealEditor save shape — include per-gram macros + gpdu so
          // USDA / custom-macro ingredients keep their numbers on create.
          ingredients: m.ingredients.map((ing, idx) => ({
            core_food_id: ing.core_food_id,
            amount_grams: Number(ing.amount_grams) || 0,
            display_qty: ing.display_qty ?? null,
            display_unit_id: ing.display_unit_id || null,
            grams_per_display_unit: ing.grams_per_display_unit ?? null,
            display_text: ing.display_text || null,
            prep_note: ing.prep_note || null,
            is_optional: !!ing.is_optional,
            sort_order: ing.sort_order ?? idx,
            cal_per_g: ing.cal_per_g ?? null,
            protein_per_g: ing.protein_per_g ?? null,
            carbs_per_g: ing.carbs_per_g ?? null,
            fat_per_g: ing.fat_per_g ?? null,
            // Explicit macro provenance (never inferred server-side).
            macros_source: ing.macros_source ?? null,
          })),
        })),
      };
      const res = await fetch(`${backendUrl}/api/admin/meal-import/commit`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || `Commit failed (HTTP ${res.status})`); return; }
      setConfirmResult(data);
      const createdCount = data.created_count;
      const failedCount = data.failed_count;
      if (createdCount === 0) {
        toast.error(`No meals created${failedCount > 0 ? ` — ${failedCount} failed` : ''}`);
      } else if (failedCount > 0) {
        toast.warning(`Imported ${createdCount} meal${createdCount === 1 ? '' : 's'} — ${failedCount} failed`);
      } else {
        toast.success(`Imported ${createdCount} meal${createdCount === 1 ? '' : 's'}`);
      }
    } catch (e) {
      console.error('[MealImport] commit error', e);
      toast.error('Network error — see console');
    } finally {
      setUploading(false);
    }
  };

  const downloadFromEndpoint = async (path, filename) => {
    try {
      const res = await fetch(`${backendUrl}${path}`, { headers: authHeaders });
      if (!res.ok) { toast.error(`Download failed (HTTP ${res.status})`); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), { href: url, download: filename });
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); console.error(e); }
  };

  // -------- Editing helpers (all immutable state updates) --------
  const updateMealInput = (mealKey, field, value) => {
    setMeals((prev) => prev.map((m) => m.meal_key === mealKey
      ? { ...m, meal_input: { ...m.meal_input, [field]: value } }
      : m));
  };
  const updateIngredient = (mealKey, idx, patch) => {
    setMeals((prev) => prev.map((m) => {
      if (m.meal_key !== mealKey) return m;
      const ingredients = m.ingredients.map((ing, i) => i === idx ? { ...ing, ...patch } : ing);
      return { ...m, ingredients };
    }));
  };
  const removeIngredient = (mealKey, idx) => {
    setMeals((prev) => prev.map((m) => m.meal_key === mealKey
      ? { ...m, ingredients: m.ingredients.filter((_, i) => i !== idx) }
      : m));
  };
  const addEmptyIngredient = (mealKey) => {
    setMeals((prev) => prev.map((m) => {
      if (m.meal_key !== mealKey) return m;
      return { ...m, ingredients: [...m.ingredients, blankIngredient(m.ingredients.length)] };
    }));
  };
  const toggleInclude = (mealKey) => {
    setMeals((prev) => prev.map((m) => m.meal_key === mealKey ? { ...m, _included: !m._included } : m));
  };
  const toggleExpand = (mealKey) => {
    setMeals((prev) => prev.map((m) => m.meal_key === mealKey ? { ...m, _expanded: !m._expanded } : m));
  };

  // ================= RENDER =================
  const includedCount = (meals || []).filter((m) => m._included).length;
  const includedWithErrors = (meals || []).filter((m) => m._included && (m.errors || []).length > 0).length;
  const previewMeal = previewMealKey ? (meals || []).find((m) => m.meal_key === previewMealKey) : null;

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        <div className="px-4 pb-4 flex items-center gap-3">
          <button onClick={() => navigate('/admin/meals')} className="p-2 -ml-2 rounded-full hover:bg-accent/10" data-testid="meal-import-back">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading font-bold text-xl">Bulk Meal Import</h1>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Step 1 — Downloads */}
        {!confirmResult && (
          <section className="bg-card border border-border/40 rounded-xl p-4 space-y-3">
            <header className="flex items-center gap-2">
              <Download size={18} className="text-accent" />
              <h2 className="font-heading font-semibold text-lg">1. Prepare your CSV</h2>
            </header>
            <div className="grid sm:grid-cols-2 gap-2">
              <Button variant="outline" className="h-11 justify-start"
                onClick={() => downloadFromEndpoint('/api/admin/meal-import/template', 'fitxess_meal_import_template.csv')}
                data-testid="download-template-btn">
                <FileText size={16} className="mr-2" /> Download CSV template
              </Button>
              <Button variant="outline" className="h-11 justify-start"
                onClick={() => downloadFromEndpoint('/api/admin/foods-core/export', 'fitxess_foods_core.csv')}
                data-testid="download-foods-core-btn">
                <Database size={16} className="mr-2" /> Download foods_core list
              </Button>
            </div>
          </section>
        )}

        {/* Step 2 — Upload */}
        {!confirmResult && (
          <section className="bg-card border border-border/40 rounded-xl p-4 space-y-3">
            <header className="flex items-center gap-2">
              <Upload size={18} className="text-accent" />
              <h2 className="font-heading font-semibold text-lg">2. Upload & preview</h2>
            </header>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handlePickFile}
              className="hidden" data-testid="meal-import-file-input" />
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" className="h-11 flex-1 justify-start"
                onClick={() => fileInputRef.current?.click()} data-testid="pick-csv-btn">
                <FileText size={16} className="mr-2" />
                {file ? file.name : 'Choose CSV file'}
              </Button>
              <Button disabled={!file || uploading} onClick={runDryRun}
                className="h-11 bg-accent text-white" data-testid="dry-run-btn">
                {uploading && !meals ? 'Validating…' : 'Run preview'}
              </Button>
            </div>
          </section>
        )}

        {/* Parse errors */}
        {parseErrors.length > 0 && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium text-red-500 mb-1">
              <XCircle size={16} /> CSV structural errors
            </div>
            <ul className="list-disc list-inside space-y-0.5">
              {parseErrors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
            </ul>
          </div>
        )}

        {/* Review & edit */}
        {meals && meals.length > 0 && !confirmResult && (
          <section className="space-y-3" data-testid="import-review">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-lg">3. Review & edit</h2>
              <div className="text-sm text-muted-foreground">
                <strong>{includedCount}</strong> included · {meals.length} total
              </div>
            </div>

            <div className="space-y-2">
              {meals.map((m) => (
                <MealReviewCard
                  key={m.meal_key}
                  meal={m}
                  uniquenessGroups={uniquenessGroups}
                  existingTags={existingTags}
                  onToggleInclude={() => toggleInclude(m.meal_key)}
                  onToggleExpand={() => toggleExpand(m.meal_key)}
                  onPreview={() => setPreviewMealKey(m.meal_key)}
                  onUpdateField={(field, val) => updateMealInput(m.meal_key, field, val)}
                  onUpdateIngredient={(idx, patch) => updateIngredient(m.meal_key, idx, patch)}
                  onRemoveIngredient={(idx) => removeIngredient(m.meal_key, idx)}
                  onAddIngredient={() => addEmptyIngredient(m.meal_key)}
                />
              ))}
            </div>

            {/* Commit */}
            <div className="sticky bottom-[calc(4rem+env(safe-area-inset-bottom))] bg-background/90 backdrop-blur-xl pt-3 pb-1 -mx-4 px-4 border-t border-border/40">
              {includedWithErrors > 0 && (
                <div className="text-xs text-red-500 mb-2">
                  {includedWithErrors} included meal{includedWithErrors === 1 ? '' : 's'} still have errors — fix or exclude before creating.
                </div>
              )}
              <Button disabled={uploading || includedCount === 0 || includedWithErrors > 0}
                onClick={commitEdits} className="h-11 w-full bg-accent text-white"
                data-testid="confirm-import-btn">
                {uploading ? 'Creating meals…' : `Create ${includedCount} meal${includedCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </section>
        )}

        {/* Result */}
        {confirmResult && (
          <motion.section
            initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/40 rounded-xl p-4 space-y-3"
            data-testid="import-result">
            <header className="flex items-center gap-2">
              <CheckCircle2 size={18} className="text-accent" />
              <h2 className="font-heading font-semibold text-lg">Result</h2>
            </header>
            <div className="text-sm">
              Created <strong>{confirmResult.created_count}</strong> meal{confirmResult.created_count === 1 ? '' : 's'}.
              {confirmResult.failed_count > 0 && <span> {confirmResult.failed_count} failed.</span>}
            </div>
            {confirmResult.created?.length > 0 && (
              <ul className="text-xs space-y-1 text-muted-foreground">
                {confirmResult.created.map((m) => (
                  <li key={m.meal_id}>
                    ✓ <strong className="text-foreground">{m.meal_title}</strong>{' '}
                    · <code className="px-1 rounded bg-muted">{m.meal_id}</code>
                    {' '}· {m.computed_macros.calories} cal
                  </li>
                ))}
              </ul>
            )}
            {confirmResult.failed?.length > 0 && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-xs space-y-1">
                {confirmResult.failed.map((f, i) => (
                  <div key={i}><strong>{f.meal_key}:</strong> {Array.isArray(f.errors) ? f.errors.join('; ') : String(f.errors)}</div>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 h-11"
                onClick={() => { setFile(null); setMeals(null); setParseErrors([]); setConfirmResult(null); }}
                data-testid="import-another-btn">Import another CSV</Button>
              <Button className="flex-1 h-11 bg-accent text-white" onClick={() => navigate('/admin/meals')}
                data-testid="view-meals-btn">View meals list</Button>
            </div>
          </motion.section>
        )}
      </div>

      {/* User-view preview modal */}
      {previewMeal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="user-preview-modal">
          <div className="bg-background w-full max-w-md max-h-[95vh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">User view preview</span>
              <button onClick={() => setPreviewMealKey(null)} className="p-1 rounded-full hover:bg-accent/10" data-testid="close-preview-btn">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <MealPreviewRenderer
                meal_input={previewMeal.meal_input}
                ingredients={previewMeal.ingredients}
                computed_macros={previewMeal.computed_macros}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ====================================================================
// Helpers
// ====================================================================

/** Normalize a dry-run ingredient so the rest of the UI can treat every
 *  ingredient identically, regardless of whether it came from the CSV, a
 *  typeahead pick, or the "Add ingredient" blank row. */
function hydrateIngredient(ing) {
  const pgm = ing.resolved_per_gram_macros || {};
  return {
    core_food_id: ing.core_food_id || '',
    amount_grams: ing.amount_grams || 0,
    display_qty: ing.display_qty ?? null,
    display_unit_id: ing.display_unit_id || null,
    grams_per_display_unit: ing.grams_per_display_unit
      ?? (ing.display_qty > 0 && ing.amount_grams ? Math.round((ing.amount_grams / ing.display_qty) * 10000) / 10000 : null),
    display_text: ing.display_text || ing.resolved_food_name || '',
    prep_note: ing.prep_note || '',
    is_optional: !!ing.is_optional,
    sort_order: ing.sort_order ?? 0,
    // Per-gram macros — use what the dry-run resolved, if any
    cal_per_g: pgm.cal_per_g ?? null,
    protein_per_g: pgm.protein_per_g ?? null,
    carbs_per_g: pgm.carbs_per_g ?? null,
    fat_per_g: pgm.fat_per_g ?? null,
    // Provenance: a Foods-Core-resolved row is 'core'; anything unresolved /
    // import-supplied is 'inline'. (CSV v1 supplies no explicit macros, so a
    // resolved row is genuinely Core-derived.)
    macros_source: provenanceOnImportHydrate(!!ing.resolved_found),
    // UI-only helpers
    resolved_found: !!ing.resolved_found,
    resolved_food_name: ing.resolved_food_name || null,
    portion_options: ing.resolved_portion_options
      || [{ id: 'g', label: 'grams', grams_per_unit: 1 }],
  };
}

function blankIngredient(sortOrder) {
  return {
    core_food_id: '', amount_grams: 0,
    display_qty: null, display_unit_id: null, grams_per_display_unit: null,
    display_text: '', prep_note: '', is_optional: false, sort_order: sortOrder,
    cal_per_g: null, protein_per_g: null, carbs_per_g: null, fat_per_g: null,
    resolved_found: false, resolved_food_name: null,
    macros_source: null,
    portion_options: [{ id: 'g', label: 'grams', grams_per_unit: 1 }],
  };
}


// ====================================================================
// Per-meal review card — mirrors MealEditor's field set
// ====================================================================
function MealReviewCard({
  meal, uniquenessGroups, existingTags,
  onToggleInclude, onToggleExpand, onPreview,
  onUpdateField, onUpdateIngredient, onRemoveIngredient, onAddIngredient,
}) {
  const { meal_input: mi, ingredients, computed_macros, errors = [], _included, _expanded, meal_key } = meal;
  const hasErrors = errors.length > 0;

  // Tag input state — local so each card has its own input
  const [tagInput, setTagInput] = useState('');
  const addTag = (t) => {
    const val = (t || '').trim();
    if (!val || (mi.tags || []).includes(val)) return;
    onUpdateField('tags', [...(mi.tags || []), val]);
    setTagInput('');
  };
  const removeTag = (t) => onUpdateField('tags', (mi.tags || []).filter((x) => x !== t));
  const toggleCookware = (id) => {
    const cur = mi.required_cookware || [];
    onUpdateField('required_cookware', cur.includes(id) ? cur.filter((c) => c !== id) : [...cur, id]);
  };

  return (
    <div className={`border rounded-xl overflow-hidden ${hasErrors ? 'border-red-500/40' : 'border-border/40'} ${!_included ? 'opacity-50' : ''}`}>
      {/* Collapsed header row */}
      <div className="flex items-center gap-2 p-3 bg-card">
        <input type="checkbox" checked={_included} onChange={onToggleInclude}
          className="w-4 h-4 accent-[hsl(var(--accent))]"
          data-testid={`include-${meal_key}`} />
        <button onClick={onToggleExpand} className="flex-1 min-w-0 text-left flex items-center gap-2"
          data-testid={`expand-${meal_key}`}>
          {_expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {hasErrors ? <AlertTriangle size={14} className="text-red-500 shrink-0" /> : <CheckCircle2 size={14} className="text-accent shrink-0" />}
              <span className="font-medium truncate">{mi.title || <em className="text-muted-foreground">(no title)</em>}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {mi.meal_type || '?'} · {ingredients.length} ing · {computed_macros.calories} cal · P {computed_macros.protein} · C {computed_macros.carbs} · F {computed_macros.fat}
            </div>
          </div>
        </button>
        <Button variant="ghost" size="sm" onClick={onPreview} data-testid={`preview-${meal_key}`}>
          <Eye size={14} className="mr-1" /> Preview
        </Button>
      </div>

      {/* Errors (always visible if present) */}
      {hasErrors && (
        <div className="px-3 pb-2 text-xs text-red-500 space-y-0.5">
          {errors.map((e, i) => <div key={i}>• {e}</div>)}
        </div>
      )}

      {/* Expanded editor */}
      {_expanded && (
        <div className="p-3 bg-background/50 border-t border-border/40 space-y-4">
          {/* Title + basics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Title *</Label>
              <Input value={mi.title || ''} onChange={(e) => onUpdateField('title', e.target.value)}
                className="h-9 mt-1" data-testid={`title-${meal_key}`} />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <select value={mi.meal_type || 'lunch'} onChange={(e) => onUpdateField('meal_type', e.target.value)}
                className="w-full h-9 px-2 rounded-lg bg-secondary/30 mt-1 text-sm"
                data-testid={`type-${meal_key}`}>
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Servings</Label>
              <Input type="number" min="1" value={mi.servings ?? 1}
                onChange={(e) => onUpdateField('servings', parseInt(e.target.value) || 1)}
                className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-xs">Cook time (min)</Label>
              <Input type="number" min="0" value={mi.cook_time_min ?? 0}
                onChange={(e) => onUpdateField('cook_time_min', parseInt(e.target.value) || 0)}
                className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-xs">Difficulty</Label>
              <select value={mi.difficulty || ''} onChange={(e) => onUpdateField('difficulty', e.target.value || null)}
                className="w-full h-9 px-2 rounded-lg bg-secondary/30 mt-1 text-sm">
                <option value="">Not specified</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          {/* Cuisine + Protein (shared dropdowns) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cuisine</Label>
              <select value={mi.cuisine || ''} onChange={(e) => onUpdateField('cuisine', e.target.value || null)}
                className="w-full h-9 px-2 rounded-lg bg-secondary/30 mt-1 text-sm"
                data-testid={`cuisine-${meal_key}`}>
                <option value="">Not specified</option>
                {CUISINE_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Protein / Meat Type</Label>
              <select value={mi.protein_type || ''} onChange={(e) => onUpdateField('protein_type', e.target.value || null)}
                className="w-full h-9 px-2 rounded-lg bg-secondary/30 mt-1 text-sm"
                data-testid={`protein-${meal_key}`}>
                <option value="">Not specified</option>
                {PROTEIN_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Cookware multi-chip */}
          <div>
            <Label className="text-xs">Required Cookware</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
              {COOKWARE_OPTIONS.map((opt) => {
                const active = (mi.required_cookware || []).includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggleCookware(opt.id)}
                    className={`px-3 py-2 rounded-lg text-xs transition-colors ${active ? 'bg-accent text-white' : 'bg-secondary/30 hover:bg-secondary/50'}`}
                    data-testid={`cookware-${meal_key}-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {(mi.required_cookware || []).length === 0 && (
              <p className="text-[10px] text-red-500 mt-1">Select at least one (or "No Cook Required").</p>
            )}
          </div>

          {/* Uniqueness group + repeat rules */}
          <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 space-y-3">
            <div className="flex items-center gap-2">
              <RefreshCcw size={14} className="text-blue-400" />
              <Label className="text-xs text-blue-300">Uniqueness Group & Repeat Rules</Label>
            </div>
            <div>
              <input
                list={`uniqueness-options-${meal_key}`}
                value={mi.uniqueness_group || ''}
                onChange={(e) => onUpdateField('uniqueness_group', e.target.value || null)}
                placeholder="e.g., Smoothies, Salads, Wraps…"
                className="w-full h-9 px-2 rounded-lg bg-secondary/30 text-sm border border-blue-500/20"
                data-testid={`uniqueness-${meal_key}`}
              />
              <datalist id={`uniqueness-options-${meal_key}`}>
                {uniquenessGroups.map((g) => <option key={g} value={g} />)}
              </datalist>
              <p className="text-[10px] text-muted-foreground mt-1">
                Type a new name to create a group on the fly.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!mi.no_repeat_same_day}
                  onChange={(e) => onUpdateField('no_repeat_same_day', e.target.checked)}
                  className="w-4 h-4 accent-[hsl(var(--accent))]"
                  data-testid={`norepeat-${meal_key}`}
                />
                No repeat same day
              </label>
              <div>
                <Label className="text-[10px]">Cooldown days</Label>
                <Input
                  type="number" min="0"
                  value={mi.cooldown_days ?? ''}
                  onChange={(e) => onUpdateField('cooldown_days', e.target.value === '' ? null : Number(e.target.value))}
                  className="h-8 mt-0.5 text-xs"
                  placeholder="(blank = default)"
                  data-testid={`cooldown-${meal_key}`}
                />
              </div>
            </div>
          </div>

          {/* Image URL + thumbnail */}
          <FieldImageURL label="Image URL" value={mi.image_url || ''} onChange={(v) => onUpdateField('image_url', v || null)} />

          {/* Tags chip editor */}
          <div>
            <Label className="text-xs">Tags</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }}
                placeholder="Type tag, press Enter"
                list={`tag-options-${meal_key}`}
                className="h-9 text-sm"
                data-testid={`tag-input-${meal_key}`}
              />
              <datalist id={`tag-options-${meal_key}`}>
                {existingTags.map((t) => <option key={t} value={t} />)}
              </datalist>
              <Button variant="outline" className="h-9" onClick={() => addTag(tagInput)}>
                <Plus size={14} />
              </Button>
            </div>
            {(mi.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {mi.tags.map((t) => (
                  <span key={t} className="px-2 py-0.5 bg-accent/10 text-accent rounded-full text-xs flex items-center gap-1">
                    {t}
                    <button onClick={() => removeTag(t)} className="hover:text-red-500" aria-label={`Remove ${t}`}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Instructions */}
          <FieldList
            label="Instructions (one step per line)"
            value={mi.instructions || []}
            onChange={(v) => onUpdateField('instructions', v)}
          />

          {/* Ingredients */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold">Ingredients ({ingredients.length})</h4>
              <Button variant="outline" size="sm" onClick={onAddIngredient} data-testid={`add-ing-${meal_key}`}>
                <Plus size={12} className="mr-1" /> Add ingredient
              </Button>
            </div>
            <div className="space-y-2">
              {ingredients.map((ing, idx) => (
                <IngredientEditor
                  key={idx}
                  ing={ing}
                  onChange={(patch) => onUpdateIngredient(idx, patch)}
                  onRemove={() => onRemoveIngredient(idx)}
                />
              ))}
              {ingredients.length === 0 && (
                <div className="text-xs text-muted-foreground italic">No ingredients — use "Add ingredient" above.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ====================================================================
// Ingredient row editor — typeahead search in empty state, full editor once
// a food is selected. Mirrors MealEditor's IngredientRow behavior.
// ====================================================================
function IngredientEditor({ ing, onChange, onRemove }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef(null);

  const hasFood = !!(ing.core_food_id || ing.resolved_food_name || ing.display_text);

  // Debounced typeahead (same 300ms cadence as MealEditor)
  const handleSearch = useCallback((q) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(() => {
      setSearching(true);
      adminAPI.searchCoreFoods(q, 20)
        .then((res) => setSearchResults(res.data || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
  }, []);

  const pickFood = (food) => {
    const defaultGrams = ing.amount_grams > 0 ? ing.amount_grams : 100;
    onChange({
      core_food_id: food.food_id,
      amount_grams: defaultGrams,
      display_qty: defaultGrams,
      display_unit_id: 'g',
      grams_per_display_unit: 1,
      display_text: ing.display_text || food.name,
      cal_per_g: food.cal_per_g || (food.macros_per_100g?.calories || 0) / 100,
      protein_per_g: food.protein_per_g || (food.macros_per_100g?.protein || 0) / 100,
      carbs_per_g: food.carbs_per_g || (food.macros_per_100g?.carbs || 0) / 100,
      fat_per_g: food.fat_per_g || (food.macros_per_100g?.fat || 0) / 100,
      portion_options: food.portion_options || [{ id: 'g', label: 'grams', grams_per_unit: 1 }],
      resolved_found: true,
      resolved_food_name: food.name,
      // Selecting a Core Food => the recipe uses Foods Core nutrition.
      macros_source: CORE,
      // Carry inherited Foods Core metadata for the read-only context badge.
      resolved_grocery_category: food.grocery_category ?? null,
      resolved_allow_meal_scaling: (food.allow_meal_scaling === undefined ? null : food.allow_meal_scaling),
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const clearFood = () => {
    onChange({
      core_food_id: '', resolved_found: false, resolved_food_name: null,
      cal_per_g: null, protein_per_g: null, carbs_per_g: null, fat_per_g: null,
      macros_source: null,
    });
  };

  const handleUnitChange = (unitId) => {
    const opt = (ing.portion_options || []).find((o) => o.id === unitId);
    const gpdu = opt ? opt.grams_per_unit : 1;
    const grams = ing.amount_grams || 0;
    const qty = gpdu > 0 ? Math.round((grams / gpdu) * 100) / 100 : grams;
    onChange({ display_unit_id: unitId, grams_per_display_unit: gpdu, display_qty: qty });
  };

  const handleQtyChange = (qty) => {
    const grams = ing.amount_grams || 0;
    const newGpdu = qty > 0 ? Math.round((grams / qty) * 10000) / 10000 : 0;
    onChange({ display_qty: qty, grams_per_display_unit: newGpdu });
  };

  const handleGramsChange = (grams) => {
    const gpdu = ing.grams_per_display_unit || 1;
    const qty = gpdu > 0 ? Math.round((grams / gpdu) * 100) / 100 : grams;
    onChange({ amount_grams: grams, display_qty: qty });
  };

  // --- Search-mode rendering ---
  if (!hasFood) {
    return (
      <div className="border border-border/40 rounded-lg p-2 space-y-2" data-testid="ingredient-search-mode">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search foods (e.g., chicken, rice, broccoli)…"
            className="pl-8 h-9 text-sm"
            data-testid="ingredient-search-input"
          />
          {searching && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        {searchResults.length > 0 && (
          <div className="max-h-48 overflow-y-auto bg-secondary/20 rounded-lg p-1.5 space-y-1 border border-border/40">
            {searchResults.map((food) => (
              <button
                key={food.food_id}
                onClick={() => pickFood(food)}
                className="w-full p-2 text-left text-xs hover:bg-accent/10 rounded transition-colors"
                data-testid={`ingredient-search-result-${food.food_id}`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium">{food.name}</span>
                  <span className="text-[10px] text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded">
                    {Math.round(food.macros_per_100g?.calories || 0)} cal/100g
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {Math.round(food.macros_per_100g?.protein || 0)}g P · {Math.round(food.macros_per_100g?.carbs || 0)}g C · {Math.round(food.macros_per_100g?.fat || 0)}g F per 100g
                </div>
              </button>
            ))}
          </div>
        )}
        {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-1">No foods found for "{searchQuery}"</p>
        )}
        <button onClick={onRemove} className="text-xs text-red-500 hover:underline" data-testid="remove-ingredient">
          Remove row
        </button>
      </div>
    );
  }

  // --- Selected-food rendering ---
  const macros = {
    cal: (ing.cal_per_g ?? 0) * (ing.amount_grams || 0),
    protein: (ing.protein_per_g ?? 0) * (ing.amount_grams || 0),
    carbs: (ing.carbs_per_g ?? 0) * (ing.amount_grams || 0),
    fat: (ing.fat_per_g ?? 0) * (ing.amount_grams || 0),
  };
  const unresolved = ing.core_food_id && !ing.resolved_found;

  return (
    <div className={`border rounded-lg p-2.5 space-y-2 ${unresolved ? 'border-red-500/40 bg-red-500/5' : 'border-border/40 bg-card'}`}
      data-testid="ingredient-row-selected">
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">
            {ing.resolved_food_name || ing.display_text || ing.core_food_id || '(no food)'}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {Math.round(macros.cal)} cal · {macros.protein.toFixed(1)}g P · {macros.carbs.toFixed(1)}g C · {macros.fat.toFixed(1)}g F
          </p>
          {ing.core_food_id && (
            <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{ing.core_food_id}</p>
          )}
          {unresolved && (
            <p className="text-[10px] text-red-500 mt-0.5">⚠ core_food_id not in foods_core — macros will be used as-is.</p>
          )}
          {/* Read-only inherited Foods Core metadata (e.g. "Pantry · No scaling").
              Classification lives on the Core Food record, not per-import. */}
          {!unresolved && (ing.resolved_grocery_category != null || ing.resolved_allow_meal_scaling != null) && (
            <p className="text-[10px] mt-0.5" data-testid="ingredient-core-meta">
              <span className="text-muted-foreground">{ing.resolved_grocery_category ?? 'Uncategorized'}</span>
              {ing.resolved_allow_meal_scaling != null && (
                <>
                  <span className="text-muted-foreground"> · </span>
                  <span className={ing.resolved_allow_meal_scaling ? 'text-emerald-600' : 'text-red-500'}>
                    {ing.resolved_allow_meal_scaling ? 'Scalable' : 'No scaling'}
                  </span>
                </>
              )}
            </p>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={clearFood} className="p-1 hover:bg-secondary/50 rounded" title="Change food">
            <Search size={14} />
          </button>
          <button onClick={onRemove} className="p-1 text-red-500 hover:bg-red-500/10 rounded" title="Remove" data-testid="remove-ingredient">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Optional toggle */}
      <button
        onClick={() => onChange({ is_optional: !ing.is_optional })}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium transition-colors ${
          ing.is_optional
            ? 'bg-amber-500/20 text-amber-600 border border-amber-500/30'
            : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50'
        }`}
      >
        <Tag size={10} />
        {ing.is_optional ? 'Optional' : 'Mark as optional'}
      </button>

      {/* Quantity / Unit / Grams */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px]">Qty</Label>
          <Input type="number" min="0" step="0.25" value={ing.display_qty ?? ''}
            onChange={(e) => handleQtyChange(parseFloat(e.target.value) || 0)}
            className="h-8 text-xs mt-0.5" />
        </div>
        <div>
          <Label className="text-[10px]">Unit</Label>
          <select value={ing.display_unit_id || 'g'} onChange={(e) => handleUnitChange(e.target.value)}
            className="w-full h-8 px-2 text-xs rounded-lg bg-secondary/30 mt-0.5">
            {(ing.portion_options || [{ id: 'g', label: 'grams' }]).map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}{opt.is_estimate ? ' (est.)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Grams *</Label>
          <Input type="number" min="0" value={ing.amount_grams ?? ''}
            onChange={(e) => handleGramsChange(parseFloat(e.target.value) || 0)}
            className="h-8 text-xs mt-0.5"
            data-testid="ingredient-grams" />
        </div>
      </div>

      {/* Per-gram macro override (same pattern as MealEditor) */}
      <details className="text-[11px]">
        <summary className="cursor-pointer text-muted-foreground">Edit per-gram macros</summary>
        <div className="grid grid-cols-4 gap-1.5 mt-1.5">
          <LabeledNum label="cal/g" value={ing.cal_per_g ?? 0}
            onChange={(v) => onChange({ cal_per_g: v, macros_source: INLINE })} step="0.01" />
          <LabeledNum label="pro/g" value={ing.protein_per_g ?? 0}
            onChange={(v) => onChange({ protein_per_g: v, macros_source: INLINE })} step="0.001" />
          <LabeledNum label="carbs/g" value={ing.carbs_per_g ?? 0}
            onChange={(v) => onChange({ carbs_per_g: v, macros_source: INLINE })} step="0.001" />
          <LabeledNum label="fat/g" value={ing.fat_per_g ?? 0}
            onChange={(v) => onChange({ fat_per_g: v, macros_source: INLINE })} step="0.001" />
        </div>
      </details>

      {/* Display text + prep note */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px]">Display text</Label>
          <Input value={ing.display_text || ''}
            onChange={(e) => onChange({ display_text: e.target.value })}
            className="h-8 text-xs mt-0.5" />
        </div>
        <div>
          <Label className="text-[10px]">Prep note</Label>
          <Input value={ing.prep_note || ''}
            onChange={(e) => onChange({ prep_note: e.target.value || null })}
            className="h-8 text-xs mt-0.5"
            placeholder="e.g., diced" />
        </div>
      </div>
    </div>
  );
}


// ====================================================================
// Small field primitives
// ====================================================================
function LabeledNum({ label, value, onChange, step }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</span>
      <input type="number" step={step || '0.01'} value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="w-full px-1.5 py-1 text-xs border border-border/40 rounded bg-background" />
    </label>
  );
}

/** Textarea that splits/joins on newlines — for instructions. */
function FieldList({ label, value, onChange, className = '' }) {
  const text = (value || []).join('\n');
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      <textarea
        rows={Math.max(3, (value || []).length)}
        value={text}
        onChange={(e) => onChange(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
        className="w-full px-2 py-1.5 text-xs border border-border/40 rounded bg-background"
      />
    </label>
  );
}

/** URL text input with an inline thumbnail preview. */
function FieldImageURL({ label, value, onChange }) {
  const [loadError, setLoadError] = useState(false);
  const hasValue = !!(value && value.trim());
  const onInput = (e) => { setLoadError(false); onChange(e.target.value); };
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type="text"
        value={value ?? ''}
        onChange={onInput}
        placeholder="https://…"
        className="h-9 mt-1 text-sm"
      />
      {hasValue && (
        <div className="mt-1.5">
          {loadError ? (
            <div className="h-20 w-32 rounded border border-red-500/40 bg-red-500/5 flex items-center justify-center text-[10px] text-red-500 text-center px-2">
              Image failed to load
            </div>
          ) : (
            <img
              src={value}
              alt="preview"
              onError={() => setLoadError(true)}
              onLoad={() => setLoadError(false)}
              className="h-20 w-32 object-cover rounded border border-border/40 bg-muted"
              data-testid="image-url-thumb"
            />
          )}
        </div>
      )}
    </div>
  );
}
